import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ApiKeysCard } from "@/components/ApiKeysCard";

const DAYS = [
  { v: "mon", l: "Monday" }, { v: "tue", l: "Tuesday" }, { v: "wed", l: "Wednesday" },
  { v: "thu", l: "Thursday" }, { v: "fri", l: "Friday" }, { v: "sat", l: "Saturday" }, { v: "sun", l: "Sunday" },
];
const pad = (n) => String(n).padStart(2, "0");

export default function Settings() {
  const [s, setS] = useState(null);
  const [dir, setDir] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [mail, setMail] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [counts, setCounts] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [clearing, setClearing] = useState(false);
  const [checkEmail, setCheckEmail] = useState("");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async () => {
    setChecking(true); setCheck(null);
    try {
      const { data } = await api.get("/admin/student-document-check", { params: { email: checkEmail.trim() } });
      setCheck(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };
  const loadCounts = () => api.get("/admin/data-counts").then(({ data }) => setCounts(data)).catch(() => {});
  useEffect(() => {
    api.get("/settings").then(({ data }) => setS(data));
    api.get("/facility-directory/status").then(({ data }) => setDir(data)).catch(() => {});
    api.get("/admin/email-status").then(({ data }) => setMail(data)).catch(() => {});
    loadCounts();
  }, []);
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email-test", { to: testTo || undefined });
      if (data.ok) toast.success(`Test email sent to ${data.to}`);
      else toast.error(`Not sent: ${data.detail}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const clearData = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/clear-data", { confirm });
      toast.success(`Cleared ${data.total} records. The system is ready for day one.`);
      setClearOpen(false); setConfirm(""); loadCounts();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };
  const save = async () => {
    try { await api.patch("/settings", { non_placement_escalation_days: Number(s.non_placement_escalation_days) }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveDigest = async () => {
    try {
      await api.patch("/settings", { digest_day: s.digest_day, digest_hour: Number(s.digest_hour), digest_minute: Number(s.digest_minute) });
      toast.success("Weekly digest schedule updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const { data } = await api.post("/facility-directory/sync");
      setSyncResult(data);
      if (data.ok) toast.success(`Synced: ${data.created} created, ${data.updated} updated`);
      else toast.error(`Sync incomplete: ${data.detail}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };
  if (!s) return null;
  return (
    <div className="space-y-6">
      <PageHeader title="System Settings" subtitle="Super Admin configuration" />
      <Card className="max-w-md">
        <div className="space-y-4">
          <Field label="Non-placement escalation deadline (business days)">
            <Input type="number" value={s.non_placement_escalation_days} onChange={(e) => setS({ ...s, non_placement_escalation_days: e.target.value })} data-testid="escalation-days" />
          </Field>
          <p className="text-xs text-zinc-400">If no facility accepts within this window after notification, the assigned Campus Coordinator is alerted and placement is flagged as a Non-Placement Alert.</p>
          <Button onClick={save} className="bg-cc-blue text-white" data-testid="save-settings-btn">Save Settings</Button>
        </div>
      </Card>

      <Card className="max-w-md" title="Weekly digest schedule">
        <div className="space-y-4">
          <p className="text-sm text-zinc-500">Each week, coordinators get an emailed summary of their open requests and students with overdue touchpoints. Pick when it goes out ({s.digest_timezone || "Eastern Time"}).</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Day">
              <Select value={s.digest_day || "mon"} onValueChange={(v) => setS({ ...s, digest_day: v })}>
                <SelectTrigger data-testid="digest-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Time">
              <Input type="time" data-testid="digest-time"
                value={`${pad(s.digest_hour ?? 9)}:${pad(s.digest_minute ?? 0)}`}
                onChange={(e) => { const [h, m] = e.target.value.split(":"); setS({ ...s, digest_hour: Number(h), digest_minute: Number(m) }); }} />
            </Field>
          </div>
          <Button onClick={saveDigest} className="bg-cc-blue text-white" data-testid="save-digest-btn">Save Digest Schedule</Button>
        </div>
      </Card>

      <Card className="max-w-2xl" title="Outbound email">
        <div className="space-y-3 text-sm">
          <p className="text-zinc-500">Every notification, invitation and digest is sent from this Microsoft 365 mailbox. It must be a licensed Exchange Online mailbox.</p>
          {mail && (
            <div className="grid sm:grid-cols-2 gap-2 text-xs bg-zinc-50 rounded-md p-3">
              <div>Sending mailbox: <b className="font-mono break-all" data-testid="email-mailbox">{mail.mailbox || "not set"}</b></div>
              <div>Microsoft Graph: <b className={mail.configured ? "text-cc-green" : "text-red-500"} data-testid="email-configured">{mail.configured ? "configured" : "not configured"}</b></div>
              <div className="sm:col-span-2">Links in emails point to: <span className="font-mono break-all">{mail.frontend_url}</span></div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to" />
            <Button onClick={sendTest} disabled={testing} variant="outline" data-testid="email-test-btn">{testing ? "Sending…" : "Send test email"}</Button>
          </div>
        </div>
      </Card>

      <Card className="max-w-2xl" title="Student document check">
        <div className="space-y-3 text-sm">
          <p className="text-zinc-500">Enter a student's login email to trace why their My Paperwork screen is empty — account, linked student record, placement and assigned documents.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" placeholder="student@school.edu" value={checkEmail}
              onChange={(e) => setCheckEmail(e.target.value)} data-testid="doc-check-email" />
            <Button onClick={runCheck} disabled={checking || !checkEmail.trim()} variant="outline" data-testid="doc-check-btn">
              {checking ? "Checking…" : "Run check"}
            </Button>
          </div>
          {check && (
            <div className="space-y-2 text-xs bg-zinc-50 rounded-md p-3" data-testid="doc-check-result">
              <p className="text-sm text-zinc-800 font-medium">{check.verdict}</p>
              <div>Login: <b>{check.user ? `${check.user.name} · ${check.user.role} · ${check.user.account_status || "active"}` : "not found"}</b></div>
              <div>Student records linked: <b>{check.students.length}</b>{check.students.map((s) => (
                <span key={s.id} className="block font-mono">{s.name} — {s.personal_email || s.school_email || "no email"} {s.user_id ? "(linked)" : "(NOT linked)"}</span>
              ))}</div>
              <div>Placements: <b>{check.placements.length}</b>{check.placements.map((p) => (
                <span key={p.id} className="block font-mono">{p.placement_status} · {p.facility || "no facility"} · {p.documents} documents</span>
              ))}</div>
              <div>Packet configured: <b>{check.packet.name || "none"}</b> with <b>{check.packet.items}</b> documents</div>
            </div>
          )}
        </div>
      </Card>

      <Card className="max-w-2xl border-red-200" title="Data management">
        <div className="space-y-3 text-sm">
          <p className="text-zinc-500">
            Clear all demo and test data so the system starts fresh on day one. This removes schools, students,
            placement requests, paperwork, touchpoints, events, messages, notifications and every locally-invited login.
          </p>
          <p className="text-xs text-zinc-500">
            <b>Always kept:</b> the SharePoint facility directory, system settings, notification &amp; paperwork templates,
            Lists &amp; Options, the Super Admin, and every Microsoft (SSO) account.
          </p>
          {counts && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs bg-zinc-50 rounded-md p-3" data-testid="data-counts">
              {[["Schools", counts.clearable.schools], ["Students", counts.clearable.students],
                ["Placement requests", counts.clearable.placement_requests], ["Touchpoints", counts.clearable.touchpoints],
                ["Engagement events", counts.clearable.engagement_events], ["Notifications", counts.clearable.notifications],
                ["Paperwork assignments", counts.clearable.paperwork_assignments], ["Logins to remove", counts.users.removable],
                ["Logins protected", counts.users.protected]].map(([l, n]) => (
                <div key={l}>{l}: <b>{n ?? 0}</b></div>
              ))}
              <div className="col-span-2 sm:col-span-3 pt-1 border-t text-zinc-500">
                Facilities kept: <b>{counts.kept.facilities}</b> · total records to remove: <b data-testid="data-total">{counts.total_clearable + counts.users.removable}</b>
              </div>
            </div>
          )}
          <Button onClick={() => { setConfirm(""); setClearOpen(true); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="clear-data-btn">
            Clear all demo / test data
          </Button>
        </div>
      </Card>

      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent data-testid="clear-data-dialog">
          <DialogHeader>
            <DialogTitle>Clear all demo / test data?</DialogTitle>
            <DialogDescription>
              This permanently deletes {counts ? counts.total_clearable + counts.users.removable : "all"} records —
              schools, students, placements, paperwork, touchpoints, events, messages, notifications and locally-invited
              logins. Facilities, settings, templates, Lists &amp; Options, the Super Admin and Microsoft (SSO) accounts are kept.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Field label='Type DELETE to confirm'>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" data-testid="clear-data-confirm" />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClearOpen(false)} data-testid="clear-data-cancel">Cancel</Button>
            <Button onClick={clearData} disabled={confirm !== "DELETE" || clearing}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="clear-data-confirm-btn">
              {clearing ? "Clearing…" : "Yes, clear everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApiKeysCard />

      <Card className="max-w-2xl" title="Facility Directory (SharePoint)">
        <div className="space-y-3 text-sm">
          <p className="text-zinc-500">Sync facilities from the SharePoint Facility List and resolve DOR / Area Manager emails from the company security groups.</p>
          {dir && (
            <div className="grid grid-cols-2 gap-2 text-xs bg-zinc-50 rounded-md p-3">
              <div>Microsoft Graph: <b className={dir.graph_configured ? "text-cc-green" : "text-red-500"}>{dir.graph_configured ? "configured" : "not configured"}</b></div>
              <div>SharePoint site: <b className={dir.sharepoint_configured ? "text-cc-green" : "text-amber-600"}>{dir.sharepoint_configured ? "configured" : "needs site URL"}</b></div>
              <div>List ID: <span className="font-mono">{dir.facility_list_id?.slice(0, 8)}…</span></div>
              <div>DOR group: <span className="font-mono">{dir.dor_group_email}</span></div>
              <div>Site: <span className="font-mono">{dir.site_host || "—"}{dir.site_path || ""}</span></div>
              <div>AM group: <span className="font-mono">{dir.am_group_email}</span></div>
            </div>
          )}
          <Button onClick={sync} disabled={syncing} className="bg-cc-blue text-white" data-testid="sync-directory-btn">{syncing ? "Syncing…" : "Sync from SharePoint"}</Button>
          {syncResult && (
            <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-md p-3 overflow-x-auto" data-testid="sync-result">{JSON.stringify(syncResult, null, 2)}</pre>
          )}
          {dir && !dir.sharepoint_configured && (
            <p className="text-xs text-amber-600">To enable live sync, set SHAREPOINT_SITE_HOST and SHAREPOINT_SITE_PATH in the backend environment, and ensure admin consent for Sites.Read.All &amp; GroupMember.Read.All.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
